import { forwardRef, type AnchorHTMLAttributes } from 'react';
import {
  Link as RouterLink,
  NavLink as RouterNavLink,
  type LinkProps as RouterLinkProps,
  type NavLinkProps,
} from 'react-router-dom';
import { useRipple } from './use-ripple';

type Appearance = 'text' | 'primary' | 'secondary' | 'ghost' | 'bare';
type Shared = { appearance?: Appearance; fullWidth?: boolean };
export type LinkProps = Shared &
  (RouterLinkProps | (AnchorHTMLAttributes<HTMLAnchorElement> & { href: string; to?: never }));
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
  {
    appearance = 'text',
    fullWidth = false,
    className = '',
    children,
    onPointerDown,
    onClick,
    ...props
  },
  ref,
) {
  const ripple = useRipple(appearance === 'text' || appearance === 'bare');
  const classes =
    `${appearance === 'text' ? 'ui-link' : appearance === 'bare' ? 'ui-link--bare' : `ui-button ui-button--${appearance} ui-button--md`}${fullWidth ? ' ui-button--full' : ''} ${className}`.trim();
  const common = {
    ref,
    className: classes,
    onPointerDown: (event: React.PointerEvent<HTMLAnchorElement>) => {
      onPointerDown?.(event);
      if (!event.defaultPrevented) ripple.pointerDown(event);
    },
    onClick: (event: React.MouseEvent<HTMLAnchorElement>) => {
      ripple.click(event);
      onClick?.(event);
    },
  };
  const content = (
    <>
      {children}
      {appearance !== 'text' && appearance !== 'bare' && ripple.ripples}
    </>
  );
  return 'to' in props && props.to !== undefined ? (
    <RouterLink {...props} {...common}>
      {content}
    </RouterLink>
  ) : (
    <a {...props} {...common}>
      {content}
    </a>
  );
});
export function NavLink({ className, children, ...props }: NavLinkProps): JSX.Element {
  return (
    <RouterNavLink
      {...props}
      className={(state) =>
        `ui-nav-link${state.isActive ? ' active' : ''} ${typeof className === 'function' ? className(state) : (className ?? '')}`.trim()
      }
    >
      {children}
    </RouterNavLink>
  );
}
